import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";
import { useStudio } from "@/context/StudioContext";

export default function Footer() {
  const { openBooking, openSample } = useStudio();

  // Single merged list — mixes route links and action buttons. `to`
  // navigates via React Router, `action` fires a callback (Booking /
  // Sample dialogs).
  const LINKS = [
    { label: "The Collection", to: "/collection" },
    { label: "Visualise", to: "/visualise" },
    { label: "The Archive", to: "/archive" },
    { label: "Journal", to: "/insights" },
    { label: "About", to: "/about" },
    { label: "Request Appointment", action: openBooking },
    { label: "Request a Sample", action: openSample },
    { label: "Contact", to: "/contact" },
  ];

  const ELEMENT_LINKS = [
    { label: "Timber", to: "/timber" },
    { label: "Carpet", to: "/carpet" },
    { label: "Tile", to: "/tile" },
    { label: "Stone", to: "/stone" },
  ];

  const LEGAL = [
    { label: "Privacy Policy", to: "/privacy" },
    { label: "Terms of Use", to: "/terms" },
  ];

  const linkClass =
    "link-underline text-[#D6D1CA]/85 text-sm hover:text-[#F5F4F0]";

  return (
    <footer data-testid="site-footer" className="bg-[#121315] border-t border-[#8C7355]/15 pt-20 pb-10">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <p className="font-serif-display text-[#F5F4F0] text-4xl md:text-5xl font-light tracking-tight leading-none">
              My Floor Studio
            </p>
            <p className="text-[#C9A876] text-[10px] tracking-[0.35em] uppercase mt-3">Drummoyne · Sydney</p>
            <p className="text-[#D6D1CA]/70 text-sm font-body mt-8 max-w-sm">
              The masterpiece of connection — flooring as the unifying foundation of every
              considered space.
            </p>

            {/* Studio contact block */}
            <address className="not-italic text-[#D6D1CA]/70 text-sm font-body mt-8 space-y-1">
              <div>184 Victoria Road, Drummoyne NSW 2047</div>
              <div>
                <a href="tel:+61272467899" data-testid="footer-phone" className="link-underline hover:text-[#F5F4F0]" style={{ transition: "color 0.4s ease" }}>
                  02 7246 7899
                </a>
                <span className="mx-2 text-[#8C7355]/40">·</span>
                <a href="mailto:info@myfloorstudio.com.au" data-testid="footer-email" className="link-underline hover:text-[#F5F4F0]" style={{ transition: "color 0.4s ease" }}>
                  info@myfloorstudio.com.au
                </a>
              </div>
            </address>

            <a
              href="https://www.instagram.com/myfloorstudio_au/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              aria-label="My Floor Studio on Instagram"
              className="inline-flex items-center gap-3 mt-8 text-[#D6D1CA]/70 hover:text-[#C9A876]"
              style={{ transition: "color 0.4s ease" }}
            >
              <Instagram size={17} strokeWidth={1.4} />
              <span className="text-[10px] tracking-[0.3em] uppercase">@myfloorstudio_au</span>
            </a>
          </div>

          {/* Elements column — the four discipline landing pages */}
          <div className="md:col-span-3">
            <p className="text-[#C9A876] text-[10px] tracking-[0.3em] uppercase mb-5">Elements</p>
            <ul className="space-y-3">
              {ELEMENT_LINKS.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    data-testid={`footer-element-${l.to.slice(1)}`}
                    className={linkClass}
                    style={{ transition: "color 0.4s ease" }}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Explore column — one flat list covering studio + trade */}
          <div className="md:col-span-3">
            <p className="text-[#C9A876] text-[10px] tracking-[0.3em] uppercase mb-5">Explore</p>
            <ul className="space-y-3">
              {LINKS.map((l) => {
                const testId = `footer-${l.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`;
                return (
                  <li key={l.label}>
                    {l.to ? (
                      <Link
                        to={l.to}
                        data-testid={testId}
                        className={linkClass}
                        style={{ transition: "color 0.4s ease" }}
                      >
                        {l.label}
                      </Link>
                    ) : (
                      <button
                        onClick={l.action}
                        data-testid={testId}
                        className={linkClass}
                        style={{ transition: "color 0.4s ease" }}
                      >
                        {l.label}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>

            <p className="text-[#C9A876] text-[10px] tracking-[0.3em] uppercase mt-10 mb-5">Legal</p>
            <ul className="space-y-3">
              {LEGAL.map((l) => {
                const testId = `footer-${l.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`;
                return (
                  <li key={l.label}>
                    <Link to={l.to} data-testid={testId} className={linkClass} style={{ transition: "color 0.4s ease" }}>
                      {l.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-[#8C7355]/10 flex flex-col md:flex-row justify-between gap-4 text-[#D6D1CA]/60 text-xs font-body">
          <span data-testid="footer-copyright">© {new Date().getFullYear()} My Floor Studio · ABN 87 651 648 029 · All rights reserved.</span>
          <span>Timber · Carpet · Tile · Stone — Drummoyne, Sydney</span>
        </div>
      </div>
    </footer>
  );
}
